import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, distinctUntilChanged, filter, map, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, Subject, throwError} from 'rxjs';
import {environment} from '../../../environments/environment';

import {ERROR_STATUSES} from '../values/error-statuses';
import {ErrorDialogComponent} from '../../editor-interface/dialogs/error-dialog/error-dialog.component';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {
  Q9PKServerErrorResponse,
  TypedHttpErrorResponse
} from '../../shared/models/pk-server-error-response.interface';

@Injectable({
  providedIn: 'root'
})
export class TrainingsService {
  private currentRequestId: BehaviorSubject<string> = new BehaviorSubject(undefined);

  trainingAlive = new Subject();

  public currentRequestId$ = this.currentRequestId.pipe(
    filter(x => Boolean(x))
    // distinctUntilChanged(), //todo: maybe find a better way to trigger this observable on navigate
  );

  public trainingMode$ = this.currentRequestId.pipe(
    distinctUntilChanged(),
    map(val => Boolean(val))
  );

  constructor(
    private http: HttpClient,
    private dialogSvc: MatDialog,
    private router: Router
  ) {}

  getTrainingTaskDiagramList(trainingId: ITrainingRequest['id'], diagramId: string) {
    return this.http
      .get<ITrainingTask>(`${environment.API_ENDPOINT}/training/${trainingId}/diagram/${diagramId}`)
      .pipe(
        takeUntil(this.trainingAlive),
        catchError((err: any) => {
          return this.handleServerError(err, diagramId);
        })
      );
  }

  handleServerError(err: TypedHttpErrorResponse<Q9PKServerErrorResponse>, diagramId: string) {
    const {error: response} = err;

    if (response && response.status === ERROR_STATUSES.TRAINING.ERROR.TRAINING_SAME_STATUS) {
      return throwError(() => err);
    }

    if (
      (response && response.status === ERROR_STATUSES.TRAINING.ERROR.TRAINING_CANCELLED) ||
      response.status === ERROR_STATUSES.TRAINING.ERROR.NO_TRAINING ||
      response.status === ERROR_STATUSES.TRAINING.ERROR.TRAINING_TASK_ITEM_CANCELLED
    ) {
      this.dialogSvc
        .open(ErrorDialogComponent, {
          panelClass: ['q9-dialog', 'q9-dialog-danger'],
          data: {message: response.error}
        })
        .afterClosed()
        .subscribe(() => {
          this.navigateOutOfTraining(diagramId);
        });
    }

    this.wipeTraining();

    return throwError(() => err);
  }

  navigateOutOfTraining(diagramId: string) {
    return this.router.navigate(['diagram', diagramId]);
  }

  wipeTraining() {
    this.unsetCurrentId();
    this.trainingAlive.complete();
  }

  markDiagram(params) {
    const {diagramId, connectionId, checked} = params;
    const status = checked ? 'seen' : 'unSeen';

    return this.http
      .put(`${environment.API_ENDPOINT}/trainings/${connectionId}/diagram/${diagramId}/check`, {
        status
      })
      .pipe(
        catchError(err => {
          return this.handleServerError(err, diagramId);
        })
      );
  }

  signOffTask(connectionId: string, password: string) {
    return this.http
      .put(`${environment.API_ENDPOINT}/trainings/${connectionId}/sign-off`, {
        password
      })
      .pipe(catchError(this.handleError));
  }

  isTrainingMode() {
    return Boolean(this.currentRequestId.value);
  }

  getCurrentId() {
    return this.currentRequestId.value;
  }

  setCurrentId(id: ITrainingRequest['id']) {
    if (!this.trainingAlive.isStopped) {
      this.currentRequestId.next(id);
    }
  }

  unsetCurrentId() {
    this.currentRequestId.next(undefined);
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => error);
  }
}

export interface ITrainingRequest {
  _id: ObjectId;
  id: ITrainingRequest['_id']; // todo: find out which one of them gets returned
  release: ObjectId;
  team: ObjectId;
  type: 'release' | 'user' | 'diagram';
  requiredBy: ISODate;
  createdBy: ObjectId;
  createdAt: ISODate;
  updatedAt: ISODate;
}

export interface ITrainingTask {
  _id: ObjectId;
  title: string;
  description: string;
  status: 'notStarted' | 'inProgress' | 'readyToApprove' | 'completed';
  createdAt: ISODate;
  createdBy: ObjectId; // author
  requiredBy: ISODate;
  connectionId: ITrainingRequest['_id'];
  assignedTo: ObjectId; // user
  trainingItems: Array<ITrainingItem>;
  hint: {
    tooltipMessage: string;
  };
}

export interface ITrainingItem {
  versionNumber: number;
  type: 'diagram';
  status: 'notDone' | 'seen' | 'unSeen' | 'done';
  item: ITrainingItemDiagram;
  approvedDateTime?: ISODate;
}

export interface ITrainingItemDiagram {
  _id: ObjectId;
  diagramId: ObjectId;
  levelNumber: string;
  map: {
    _id: ObjectId;
    name: string;
  };
  name: string;
  state: DIAGRAM_VERSION_STATES_ENUM.MASTER;
}

export enum TRAINING_TASK_STATUSES_ENUM {
  NOT_STARTED = 'notStarted',
  IN_PROGRESS = 'inProgress',
  READY_TO_APPROVE = 'readyToApprove',
  COMPLETED = 'completed'
}

// @ts-ignore
type ISODate = string;

// @ts-ignore
type ObjectId = string;
